import { useMemo } from "react";
import { BarChart, Card } from "@tremor/react";

import { type TeamMemberWithUser } from "@fitness-app/data-models/entities/TeamMember";

import { type TrainerMetricsAggregation } from "../hooks/useChatResponseMetrics";

interface TrainerResponseTimeChartProps {
  data: TrainerMetricsAggregation[];
  trainersById: Record<string, TeamMemberWithUser>;
}

const valueFormatter = (value: number) => {
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}min`;
};

export const TrainerResponseTimeChart = ({ data, trainersById }: TrainerResponseTimeChartProps) => {
  const chartData = useMemo(() => {
    return data
      .filter((item) => item.avgBusinessMinutes !== null)
      .map((item) => {
        const trainer = trainersById[item.trainerId];
        const trainerSurname = trainer?.user?.lastName ?? item.trainerId;

        return {
          trainer: trainerSurname,
          "Średni czas (min)": item.avgBusinessMinutes ?? 0,
        };
      })
      .sort((a, b) => a["Średni czas (min)"] - b["Średni czas (min)"]);
  }, [data, trainersById]);

  if (chartData.length === 0) {
    return (
      <Card>
        <h3 className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
          Średni czas odpowiedzi według trenera
        </h3>
        <p className="text-tremor-content dark:text-dark-tremor-content mt-4">Brak danych do wyświetlenia</p>
      </Card>
    );
  }

  // Dynamic height: 28px per trainer, minimum 200px
  const chartHeight = Math.max(200, chartData.length * 28);

  return (
    <Card>
      <h3 className="text-tremor-content-strong dark:text-dark-tremor-content-strong font-medium">
        Średni czas odpowiedzi według trenera (godziny pracy)
      </h3>
      <BarChart
        className="mt-4"
        style={{ height: chartHeight }}
        data={chartData}
        index="trainer"
        categories={["Średni czas (min)"]}
        colors={["blue"]}
        valueFormatter={valueFormatter}
        layout="vertical"
        showLegend={false}
        showYAxis={true}
        showGridLines={true}
        yAxisWidth={100}
      />
    </Card>
  );
};
